import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { Tooltip } from "@mui/material";
import { ReactNode } from "react";

export interface FieldWarningIndicatorProps {
  /** Tooltip content shown on hover — string or rich ReactNode. */
  tooltip: ReactNode;
  /** Screen-reader label for the icon. */
  ariaLabel: string;
  /**
   * When the icon sits inline after a text value (e.g. "ABC ⚠️"), reserve a
   * small left margin so it doesn't crowd the value. Standalone uses (e.g.
   * AmountCell, which positions via parent Stack spacing) leave this off.
   */
  inline?: boolean;
  /**
   * Keep the tooltip open while the pointer moves onto it, so its contents are
   * clickable (e.g. the sources-disagree tooltip's copy-a-value affordance). The
   * app theme sets `disableInteractive: true` globally, so opt in here; the
   * `leaveDelay` bridges the small gap between the icon and the popup. Leave off
   * for read-only warnings.
   */
  interactive?: boolean;
}

/**
 * Generic cell-level warning icon used wherever a field needs to flag an issue
 * — a ManifestDiscrepancy (any status), a silent source disagreement, etc. The visual
 * (warning-colored ErrorOutline) and tooltip behavior are shared so the user
 * sees a single, consistent affordance regardless of what triggered it.
 *
 * Render decision lives with the caller: this component always renders when
 * mounted. Callers gate on whatever signal is meaningful for them.
 */
export function FieldWarningIndicator({
  tooltip,
  ariaLabel,
  inline = false,
  interactive = false,
}: FieldWarningIndicatorProps) {
  return (
    <Tooltip
      title={tooltip}
      placement="top"
      enterDelay={400}
      disableInteractive={!interactive}
      leaveDelay={interactive ? 200 : undefined}
    >
      <ErrorOutline
        sx={{
          fontSize: "1rem",
          color: "warning.main",
          flexShrink: 0,
          ...(inline ? { ml: 0.5 } : {}),
        }}
        aria-label={ariaLabel}
      />
    </Tooltip>
  );
}
